/**
 * Position-faithful mark input rules.
 *
 * Replaces @tiptap/core's stock `markInputRule`-based rules for
 * bold / italic / strike / code. The stock rules compute their replacement
 * range by STRING-LENGTH arithmetic against `getTextContentFromNodes()`,
 * which substitutes an atom's `renderText()` output (a mention chip's
 * "@Label" is N characters for 1 document position) — so any match near a
 * mention chip mis-ranges (silently eats characters) or resolves a negative
 * position and THROWS out of `handleTextInput`, swallowing the keystroke.
 * The stock code regex additionally lacks the `$` anchor and leading guard
 * that bold/italic/strike carry, which ate the character before the opening
 * backtick (`` x`a` `` deleted the `x`).
 *
 * Two-phase matching fixes both families:
 *   1. The rule's `find` regex fires as a permissive trigger on the runner's
 *      (potentially polluted) text — cheap, and misses are harmless.
 *   2. The handler re-extracts a POSITION-FAITHFUL string for the current
 *      textblock — exactly 1 character per document position, with every
 *      non-text inline leaf AND every code-marked character replaced by
 *      U+FFFC (excluded from every content class) — re-runs the regex, and
 *      computes ranges from real match indices. A mention inside the
 *      delimiters is a clean no-match instead of a crash; existing code
 *      spans are opaque, so a new match can never chew into them.
 *
 * Leading context lives in a LOOKBEHIND (never consumed, never deleted):
 * bold/italic/strike keep stock "start-of-line or whitespace" semantics via
 * `(?<!\S)`; code keeps Slack-style mid-word permissiveness and only bans a
 * preceding backtick. `Code` is also flipped to `inclusive: false` so the
 * caret at the end of a code span types PLAIN text (the Slack/GChat escape)
 * instead of extending the span forever.
 *
 * Never re-enable the StarterKit built-ins for these four marks — see the
 * `[FE/Editor]` pitfall in CLAUDE.md.
 */
import Bold from '@tiptap/extension-bold'
import Code from '@tiptap/extension-code'
import Italic from '@tiptap/extension-italic'
import Strike from '@tiptap/extension-strike'
import type { MarkType } from '@tiptap/pm/model'
import { InputRule, getMarksBetween } from '@tiptap/react'

/** One character per document position for anything a mark rule must not span. */
const OPAQUE = '￼'

interface FaithfulMarkRuleSpec {
  /**
   * Anchored at the caret (`$`), leading context in a lookbehind, content in
   * capture group 1, content class excluding `￼`. The same regex serves
   * as the phase-1 trigger and the phase-2 verifier.
   */
  find: RegExp
  /** Opening delimiter, e.g. "**" or "`". */
  open: string
  /** Closing delimiter (same length assumptions as stock rules). */
  close: string
  type: MarkType
}

function faithfulMarkInputRule({ find, open, close, type }: FaithfulMarkRuleSpec) {
  return new InputRule({
    find,
    handler: ({ state, range, match: runnerMatch }) => {
      // The just-typed text isn't in the doc yet. Recover its length from the
      // runner's arithmetic (range.from = caret - (match[0].length - text.length),
      // range.to = caret), so: text.length = match[0].length - (to - from).
      const typedLength = runnerMatch[0].length - (range.to - range.from)
      // typedLength 0 also covers the IME compositionend replay (text: '') —
      // a mark typed entirely inside a composition stays literal, a known,
      // accepted delta (stock rules replayed with the same broken arithmetic).
      if (typedLength <= 0 || typedLength > close.length) return null

      const caret = range.to // a real document position, provided by PM
      const $caret = state.doc.resolve(caret)
      if (!$caret.parent.isTextblock || $caret.parent.type.spec.code) return null

      // Phase 2: position-faithful text for the block up to the caret.
      const blockStart = $caret.start()
      let faithful = ''
      $caret.parent.nodesBetween(0, $caret.parentOffset, (node, pos) => {
        if (node.isText) {
          const chunk = (node.text ?? '').slice(0, Math.max(0, $caret.parentOffset - pos))
          faithful += node.marks.some((m) => m.type.spec.code) ? OPAQUE.repeat(chunk.length) : chunk
        } else {
          // Every current inline non-text node (mention, channelMention,
          // wikilink, hardBreak, chips) is a LEAF: nodeSize 1 → exactly one
          // opaque char. A future inline node with children would skew the
          // position mapping — keep inline nodes atomic or revisit this walk.
          faithful += OPAQUE
        }
        return false
      })
      const virtual = runnerMatch[0].slice(runnerMatch[0].length - typedLength)
      const m = find.exec(faithful + virtual)
      const content = m?.[1]
      if (m == null || content == null) return null

      // Lookbehind leading context ⇒ m.index IS the opening delimiter.
      const openFrom = blockStart + m.index
      const contentFrom = openFrom + open.length
      const contentTo = contentFrom + content.length
      // The closing delimiter's in-document tail (the typed part isn't in the doc).
      const closeInDocTo = contentTo + close.length - typedLength
      if (openFrom < blockStart || closeInDocTo > caret) return null

      // Mirror stock markInputRule's excluded-marks bail (e.g. code excludes all).
      const excluded = getMarksBetween(openFrom, caret, state.doc)
        .filter((item) => {
          const ex = (item.mark.type as unknown as { excluded: MarkType[] }).excluded
          return ex.find((t) => t === type && t !== item.mark.type)
        })
        .filter((item) => item.to > contentFrom)
      if (excluded.length) return null

      const { tr } = state
      // Delete the closing delimiter first so earlier positions stay stable.
      if (closeInDocTo > contentTo) tr.delete(contentTo, closeInDocTo)
      tr.delete(openFrom, contentFrom)
      tr.addMark(openFrom, contentTo - open.length, type.create())
      // Next typed character is plain (Slack behavior).
      tr.removeStoredMark(type)
    },
  })
}

// Regexes: stock semantics with lookbehind leading context, `$`-anchored,
// content classes excluding the opaque placeholder.
const boldStarInput = /(?<!\S)\*\*(?!\s+\*\*)([^*￼]+)\*\*$/
const boldUnderscoreInput = /(?<!\S)__(?!\s+__)([^_￼]+)__$/
const italicStarInput = /(?<!\S)\*(?!\s+\*)([^*￼]+)\*$/
const italicUnderscoreInput = /(?<!\S)_(?!\s+_)([^_￼]+)_$/
const strikeInput = /(?<!\S)~~(?!\s+~~)([^~￼]+)~~$/
/** Mid-word permissive (Slack parity); only a preceding backtick suppresses it. */
const codeInput = /(?<!`)`([^`￼]+)`$/

export const BoldWithFaithfulRules = Bold.extend({
  addInputRules() {
    return [
      faithfulMarkInputRule({ find: boldStarInput, open: '**', close: '**', type: this.type }),
      faithfulMarkInputRule({
        find: boldUnderscoreInput,
        open: '__',
        close: '__',
        type: this.type,
      }),
    ]
  },
})

export const ItalicWithFaithfulRules = Italic.extend({
  addInputRules() {
    return [
      faithfulMarkInputRule({ find: italicStarInput, open: '*', close: '*', type: this.type }),
      faithfulMarkInputRule({
        find: italicUnderscoreInput,
        open: '_',
        close: '_',
        type: this.type,
      }),
    ]
  },
})

export const StrikeWithFaithfulRules = Strike.extend({
  addInputRules() {
    return [faithfulMarkInputRule({ find: strikeInput, open: '~~', close: '~~', type: this.type })]
  },
})

export const CodeWithFaithfulRules = Code.extend({
  // The caret at the end of a code span types PLAIN text instead of
  // extending the span — the missing escape that made "sometimes the
  // backticks end up inside the code" (every char typed adjacent to a span
  // inherited the mark, and input rules are disabled next to code marks).
  inclusive: false,
  addInputRules() {
    return [faithfulMarkInputRule({ find: codeInput, open: '`', close: '`', type: this.type })]
  },
})
